/**
 * Step 2 of the bulk upload: a two-column table pairing each Ryerson field ("Our Columns", a red
 * asterisk when required) with a dropdown of the file's headers ("Fields in Your File"). Signed-in
 * users also get "Save this field mapping for future uploads". `isGenericError` shows the alert set
 * when validating the file failed as a whole (`BulkUploadModal.tsx` → `onSubmit`, step 3).
 *
 * Selection and the required check live in `BulkUploadModal.tsx` (`handleDropdownSelection`,
 * `onSubmit`); the dropdown cell is `MapFieldsDataTableConfig.tsx` → `DropdownUserField`.
 * Test: `unit-tests/shared/BulkUploadModal/MapFields.spec.tsx`.
 */
import React from "react";
import { Typography } from "@ryerson/frontend.typography";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { useApplication } from "@ryerson/frontend.application";
import { MapFieldsContent } from "./MapFieldsContent";
import { MapFieldsProps } from "./BulkUploadModalDesktop";
import { SimpleTable } from "@ryerson/frontend.table";
import { DropdownUserField } from "./MapFieldsDataTableConfig";
import { useTheme } from "@ryerson/frontend.theme";
import { FileMapping } from "./BulkUpload.model";
import { Alert } from "@ryerson/frontend.notification";
import { Checkbox } from "@ryerson/frontend.form-controls";

/** Step 2: map the file's columns to Ryerson's fields; see the file header. */
export const MapFields: React.FC<MapFieldsProps> = ({
	fileFields,
	fileMapping,
	isMissingRequiredData,
	handleDropdownSelection,
	isGenericError,
	saveMapping,
	setSaveMapping,
	isLoggedIn,
}) => {
	const {
		localization: { language },
	} = useApplication();

	const content = MapFieldsContent[language || "en"];
	const { theme } = useTheme();
	const { error, secondaryBrand } = theme.colors.primary;

	return (
		<>
			<Flex direction="column">
				{isGenericError && (
					<FlexItem css={{ marginLeft: -10, marginTop: 20, marginBottom: 20 }}>
						<Alert severity="error">
							<Typography color={error} size="lg">
								{content.thereWasAProblem}
							</Typography>
						</Alert>
					</FlexItem>
				)}
				<Typography size="lg" css={{ marginBottom: 38 }}>
					{content.pleaseMatch}
				</Typography>
				<Flex
					direction="column"
					css={{
						border: "solid 1px rgba(0, 34, 85, 0.1)",
					}}
				>
					{/* Each FileMapping becomes a row keyed by the two translated column
					    titles; its other keys are filtered out. */}
					<SimpleTable
						data={fileMapping.map((mapping) =>
							Object.fromEntries(
								Object.entries(mapping)
									.map(([header, cellContents], index, collection) => {
										if (header === "ryersonColumn") {
											return [
												content.ourFields,
												<>
													<Typography color={secondaryBrand}>
														{cellContents}
													</Typography>
													{collection.find(
														([header, cellContents]) =>
															header === "required" &&
															cellContents === true
													) && (
														<Typography variant="span" color={error}>
															&nbsp;*
														</Typography>
													)}
												</>,
											];
										} else if (header === "userColumn") {
											return [
												content.fieldsInFile,

												<DropdownUserField
													key={index + "-user-field"}
													error={error}
													mappedFileFields={[
														{ value: "", display: "Select" },
													].concat(
														fileFields.map((fileField) => ({
															value: fileField,
															display: fileField,
														}))
													)}
													rowData={
														Object.fromEntries(
															collection
														) as FileMapping
													}
													isMissingRequiredData={
														content.isMissingRequiredData
													}
													handleDropdownSelection={
														handleDropdownSelection
													}
													_isMissingRequiredData={isMissingRequiredData}
												/>,
											];
										} else return [header, cellContents];
									})
									.filter(
										([header, cellContents]) =>
											header === content.fieldsInFile ||
											header === content.ourFields
									)
							)
						)}
					/>
				</Flex>
				<Flex
					css={{
						marginTop: "25px",
					}}
				>
					{isLoggedIn && (
						<Checkbox
							id="bulk-upload-save-mapping"
							value={content.saveMapping}
							checked={saveMapping}
							onChange={() => setSaveMapping(!saveMapping)}
							language={language}
							dataTestId="bulk-upload-save-mapping-fields-checkbox"
						/>
					)}
				</Flex>
			</Flex>
		</>
	);
};
